import { z } from 'zod';
import { locationMergeDecisionSchema, locationSchema } from './location.js';
import { accountIdentitySchema, userPreferencesSchema } from './profile.js';
import { canonicalJsonStringify, compareDecimalCursors, compareInstants, decimalCursorSchema, instantSchema, revisionSchema, sha256Schema, uuidSchema, } from './primitives.js';
import { routeTemplateSchema, routeVariantSchema } from './route.js';
import { shiftAggregateSchema } from './shift.js';
import { tombstoneLifecycleSchema } from './versioning.js';
import { vehicleSchema } from './vehicle.js';
export const syncEntityTypeSchema = z.enum([
    'shift_aggregate',
    'vehicle',
    'route_variant',
    'route_template',
    'user_defined_location',
    'location_merge_decision',
    'user_preferences',
]);
export const syncEntityPayloadSchema = z.discriminatedUnion('entity_type', [
    z.strictObject({ entity_type: z.literal('shift_aggregate'), value: shiftAggregateSchema }),
    z.strictObject({ entity_type: z.literal('vehicle'), value: vehicleSchema }),
    z.strictObject({ entity_type: z.literal('route_variant'), value: routeVariantSchema }),
    z.strictObject({ entity_type: z.literal('route_template'), value: routeTemplateSchema }),
    z.strictObject({ entity_type: z.literal('user_defined_location'), value: locationSchema }),
    z.strictObject({
        entity_type: z.literal('location_merge_decision'),
        value: locationMergeDecisionSchema,
    }),
    z.strictObject({ entity_type: z.literal('user_preferences'), value: userPreferencesSchema }),
]);
export const syncOperationSchema = z
    .strictObject({
    contract_version: z.literal('sync_operation.v1'),
    operation_id: uuidSchema,
    idempotency_key_sha256: sha256Schema,
    device_id: uuidSchema,
    local_profile_id: uuidSchema,
    dataset_epoch: uuidSchema,
    local_sequence: decimalCursorSchema,
    operation_kind: z.enum(['upsert', 'soft_delete', 'restore']),
    operation_origin: z.enum(['local_edit', 'json_merge_import', 'conflict_resolution']),
    entity_id: uuidSchema,
    base_revision: revisionSchema.nullable(),
    resulting_revision: revisionSchema,
    payload: syncEntityPayloadSchema,
    created_at: instantSchema,
    server_cursor: decimalCursorSchema.nullable(),
})
    .superRefine((value, context) => {
    const identity = payloadIdentity(value.payload);
    if (identity.entity_id !== value.entity_id) {
        context.addIssue({
            code: 'custom',
            path: ['entity_id'],
            message: 'entity_id must match the payload root',
        });
    }
    if (identity.revision !== value.resulting_revision) {
        context.addIssue({
            code: 'custom',
            path: ['resulting_revision'],
            message: 'resulting_revision must match the payload root revision',
        });
    }
    if (value.base_revision !== null && value.resulting_revision !== value.base_revision + 1) {
        context.addIssue({
            code: 'custom',
            path: ['resulting_revision'],
            message: 'a sync mutation must increment the base revision exactly once',
        });
    }
    if (value.base_revision === null && value.operation_kind !== 'upsert') {
        context.addIssue({
            code: 'custom',
            path: ['base_revision'],
            message: 'only creation through upsert may omit base_revision',
        });
    }
    if (value.payload.entity_type === 'shift_aggregate') {
        if (value.payload.value.dataset_epoch !== value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['payload', 'value', 'dataset_epoch'],
                message: 'aggregate and operation dataset_epoch must match',
            });
        }
    }
    if (value.payload.entity_type === 'user_defined_location' &&
        value.payload.value.origin !== 'user_defined') {
        context.addIssue({
            code: 'custom',
            path: ['payload', 'value', 'origin'],
            message: 'official catalog locations are artifacts, not user sync entities',
        });
    }
    const tombstone = identity.tombstone;
    if (value.operation_kind === 'soft_delete') {
        if (tombstone === null ||
            tombstone.deletion_operation_id !== value.operation_id ||
            tombstone.dataset_epoch !== value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['payload'],
                message: 'soft_delete requires a matching root tombstone in the same dataset epoch',
            });
        }
        if ((tombstone?.deletion_cursor ?? null) !== value.server_cursor) {
            context.addIssue({
                code: 'custom',
                path: ['server_cursor'],
                message: 'server cursor and assigned deletion cursor must be written atomically',
            });
        }
    }
    else if (tombstone !== null) {
        context.addIssue({
            code: 'custom',
            path: ['payload'],
            message: 'only soft_delete operations may carry a root tombstone',
        });
    }
});
export const pendingSyncOperationSchema = syncOperationSchema.refine((operation) => operation.server_cursor === null, {
    path: ['server_cursor'],
    message: 'a pending outbox operation cannot already have a server cursor',
});
export const deviceCursorSchema = z
    .strictObject({
    device_id: uuidSchema,
    account_identity: accountIdentitySchema,
    dataset_epoch: uuidSchema,
    last_applied_cursor: decimalCursorSchema,
    registered_at: instantSchema,
    last_seen_at: instantSchema,
    registration_expires_at: instantSchema,
    registration_status: z.enum(['active', 'expired', 'revoked']),
    requires_full_resync: z.boolean(),
})
    .superRefine((value, context) => {
    if (compareInstants(value.last_seen_at, value.registered_at) < 0) {
        context.addIssue({
            code: 'custom',
            path: ['last_seen_at'],
            message: 'last_seen_at must not precede registration',
        });
    }
    if (compareInstants(value.registration_expires_at, value.registered_at) <= 0) {
        context.addIssue({
            code: 'custom',
            path: ['registration_expires_at'],
            message: 'device registration must expire after it is created',
        });
    }
    if (value.registration_status !== 'active' && !value.requires_full_resync) {
        context.addIssue({
            code: 'custom',
            path: ['requires_full_resync'],
            message: 'expired or revoked devices cannot resume writes from an old cursor',
        });
    }
});
export const syncConflictSchema = z
    .strictObject({
    conflict_id: uuidSchema,
    entity_id: uuidSchema,
    entity_type: syncEntityTypeSchema,
    local_payload: syncEntityPayloadSchema,
    server_payload: syncEntityPayloadSchema,
    proposed_merged_payload: syncEntityPayloadSchema.nullable(),
    resolution: z.enum(['pending_user_choice', 'use_local', 'use_server', 'use_proposed_merge']),
    detected_at: instantSchema,
})
    .superRefine((value, context) => {
    const payloads = [
        value.local_payload,
        value.server_payload,
        value.proposed_merged_payload,
    ].filter((payload) => payload !== null);
    payloads.forEach((payload, index) => {
        if (payload.entity_type !== value.entity_type ||
            payloadIdentity(payload).entity_id !== value.entity_id) {
            context.addIssue({
                code: 'custom',
                path: [
                    index === 0
                        ? 'local_payload'
                        : index === 1
                            ? 'server_payload'
                            : 'proposed_merged_payload',
                ],
                message: 'all conflict payloads must describe the same entity',
            });
        }
    });
    if (value.resolution === 'use_proposed_merge' && value.proposed_merged_payload === null) {
        context.addIssue({
            code: 'custom',
            path: ['resolution'],
            message: 'proposed merge can be selected only when it exists',
        });
    }
});
export const syncPushRequestSchema = z
    .strictObject({
    contract_version: z.literal('sync_push.v1'),
    account_identity: accountIdentitySchema,
    device_id: uuidSchema,
    local_profile_id: uuidSchema,
    dataset_epoch: uuidSchema,
    known_server_cursor: decimalCursorSchema,
    operations: z.array(pendingSyncOperationSchema).max(1_000),
})
    .superRefine((value, context) => {
    const operationIds = new Set();
    const idempotencyKeys = new Set();
    let previousLocalSequence = null;
    value.operations.forEach((operation, index) => {
        if (operation.device_id !== value.device_id ||
            operation.local_profile_id !== value.local_profile_id ||
            operation.dataset_epoch !== value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['operations', index],
                message: 'push operations must match the request device, profile, and epoch',
            });
        }
        if (operationIds.has(operation.operation_id) ||
            idempotencyKeys.has(operation.idempotency_key_sha256)) {
            context.addIssue({
                code: 'custom',
                path: ['operations', index],
                message: 'push batch cannot repeat an operation or idempotency key',
            });
        }
        operationIds.add(operation.operation_id);
        idempotencyKeys.add(operation.idempotency_key_sha256);
        if (previousLocalSequence !== null &&
            compareDecimalCursors(operation.local_sequence, previousLocalSequence) <= 0) {
            context.addIssue({
                code: 'custom',
                path: ['operations', index, 'local_sequence'],
                message: 'push operations must be strictly ordered by monotonic local_sequence',
            });
        }
        previousLocalSequence = operation.local_sequence;
    });
});
export const syncPushResponseSchema = z
    .strictObject({
    contract_version: z.literal('sync_push_response.v1'),
    dataset_epoch: uuidSchema,
    server_cursor: decimalCursorSchema,
    accepted_operation_ids: z.array(uuidSchema).max(1_000),
    idempotently_replayed_operation_ids: z.array(uuidSchema).max(1_000),
    conflicts: z.array(syncConflictSchema).max(1_000),
    requires_full_resync: z.boolean(),
})
    .superRefine((value, context) => {
    const accepted = new Set(value.accepted_operation_ids);
    const replayed = new Set(value.idempotently_replayed_operation_ids);
    if (accepted.size !== value.accepted_operation_ids.length) {
        context.addIssue({
            code: 'custom',
            path: ['accepted_operation_ids'],
            message: 'accepted IDs must be unique',
        });
    }
    if (replayed.size !== value.idempotently_replayed_operation_ids.length) {
        context.addIssue({
            code: 'custom',
            path: ['idempotently_replayed_operation_ids'],
            message: 'replayed IDs must be unique',
        });
    }
    if ([...accepted].some((operationId) => replayed.has(operationId))) {
        context.addIssue({
            code: 'custom',
            message: 'an operation cannot be both newly accepted and idempotently replayed',
        });
    }
    if (value.requires_full_resync &&
        (value.accepted_operation_ids.length > 0 ||
            value.idempotently_replayed_operation_ids.length > 0)) {
        context.addIssue({
            code: 'custom',
            message: 'a rejected stale epoch cannot accept or replay writes before full resync',
        });
    }
});
export const syncPullRequestSchema = z.strictObject({
    contract_version: z.literal('sync_pull.v1'),
    account_identity: accountIdentitySchema,
    device_id: uuidSchema,
    dataset_epoch: uuidSchema,
    after_cursor: decimalCursorSchema,
    limit: z.number().int().safe().min(1).max(1_000),
});
export const syncPullResponseSchema = z
    .strictObject({
    contract_version: z.literal('sync_pull_response.v1'),
    dataset_epoch: uuidSchema,
    from_cursor: decimalCursorSchema,
    next_cursor: decimalCursorSchema,
    has_more: z.boolean(),
    operations: z.array(syncOperationSchema).max(1_000),
    requires_full_resync: z.boolean(),
})
    .superRefine((value, context) => {
    if (compareDecimalCursors(value.next_cursor, value.from_cursor) < 0) {
        context.addIssue({
            code: 'custom',
            path: ['next_cursor'],
            message: 'pull cursor cannot move backwards',
        });
    }
    let priorCursor = value.from_cursor;
    value.operations.forEach((operation, index) => {
        if (operation.dataset_epoch !== value.dataset_epoch || operation.server_cursor === null) {
            context.addIssue({
                code: 'custom',
                path: ['operations', index],
                message: 'pulled operations require the response epoch and an assigned server cursor',
            });
            return;
        }
        if (compareDecimalCursors(operation.server_cursor, priorCursor) <= 0 ||
            compareDecimalCursors(operation.server_cursor, value.next_cursor) > 0) {
            context.addIssue({
                code: 'custom',
                path: ['operations', index, 'server_cursor'],
                message: 'pulled server cursors must be strictly increasing within the response window',
            });
        }
        priorCursor = operation.server_cursor;
    });
    if (value.requires_full_resync && value.operations.length > 0) {
        context.addIssue({
            code: 'custom',
            path: ['operations'],
            message: 'incremental operations cannot be applied when full resync is required',
        });
    }
});
export function mergeSyncOperationSets(existingInput, incomingInput) {
    const existing = z.array(syncOperationSchema).parse(existingInput);
    const incoming = z.array(syncOperationSchema).parse(incomingInput);
    const byId = new Map();
    const byIdempotencyKey = new Map();
    const byDeviceSequence = new Map();
    const duplicateIds = new Set();
    const conflictingIds = new Set();
    for (const operation of [...existing, ...incoming]) {
        const prior = byId.get(operation.operation_id);
        if (prior) {
            if (canonicalJsonStringify(prior) ===
                canonicalJsonStringify(operation)) {
                duplicateIds.add(operation.operation_id);
            }
            else {
                conflictingIds.add(operation.operation_id);
            }
            continue;
        }
        const priorIdempotencyKey = byIdempotencyKey.get(operation.idempotency_key_sha256);
        if (priorIdempotencyKey) {
            conflictingIds.add(priorIdempotencyKey.operation_id);
            conflictingIds.add(operation.operation_id);
            continue;
        }
        const deviceSequenceKey = `${operation.device_id}\u0000${operation.dataset_epoch}\u0000${operation.local_sequence}`;
        const priorDeviceSequence = byDeviceSequence.get(deviceSequenceKey);
        if (priorDeviceSequence) {
            conflictingIds.add(priorDeviceSequence.operation_id);
            conflictingIds.add(operation.operation_id);
            continue;
        }
        byId.set(operation.operation_id, operation);
        byIdempotencyKey.set(operation.idempotency_key_sha256, operation);
        byDeviceSequence.set(deviceSequenceKey, operation);
    }
    return {
        operations: [...byId.values()].sort((left, right) => compareDecimalCursors(left.local_sequence, right.local_sequence) ||
            left.operation_id.localeCompare(right.operation_id)),
        duplicate_operation_ids: [...duplicateIds].sort(),
        conflicting_operation_ids: [...conflictingIds].sort(),
    };
}
export function canPurgeTombstone(tombstoneInput, deviceCursorsInput, nowInput) {
    return evaluateTombstonePurge(tombstoneInput, deviceCursorsInput, nowInput).purge_allowed;
}
export const tombstonePurgeDecisionSchema = z.strictObject({
    purge_allowed: z.boolean(),
    reasons: z.array(z.enum([
        'recovery_window_open',
        'deletion_cursor_unassigned',
        'active_device_wrong_epoch',
        'active_device_not_acknowledged',
    ])),
    blocking_device_ids: z.array(uuidSchema),
});
export function evaluateTombstonePurge(tombstoneInput, deviceCursorsInput, nowInput) {
    const tombstone = tombstoneLifecycleSchema.parse(tombstoneInput);
    const deviceCursors = z.array(deviceCursorSchema).parse(deviceCursorsInput);
    const now = instantSchema.parse(nowInput);
    const reasons = [];
    const blockingDeviceIds = new Set();
    if (compareInstants(now, tombstone.restore_until) < 0) {
        reasons.push('recovery_window_open');
    }
    if (tombstone.deletion_cursor === null) {
        reasons.push('deletion_cursor_unassigned');
    }
    const activeDevices = deviceCursors.filter((device) => device.registration_status === 'active' &&
        compareInstants(now, device.registration_expires_at) < 0);
    for (const device of activeDevices) {
        if (device.dataset_epoch !== tombstone.dataset_epoch) {
            if (!reasons.includes('active_device_wrong_epoch')) {
                reasons.push('active_device_wrong_epoch');
            }
            blockingDeviceIds.add(device.device_id);
        }
        else if (tombstone.deletion_cursor !== null &&
            compareDecimalCursors(device.last_applied_cursor, tombstone.deletion_cursor) < 0) {
            if (!reasons.includes('active_device_not_acknowledged')) {
                reasons.push('active_device_not_acknowledged');
            }
            blockingDeviceIds.add(device.device_id);
        }
    }
    return tombstonePurgeDecisionSchema.parse({
        purge_allowed: reasons.length === 0,
        reasons,
        blocking_device_ids: [...blockingDeviceIds].sort(),
    });
}
export function canDeviceWrite(deviceInput, currentDatasetEpochInput, nowInput) {
    const device = deviceCursorSchema.parse(deviceInput);
    const currentDatasetEpoch = uuidSchema.parse(currentDatasetEpochInput);
    const now = instantSchema.parse(nowInput);
    return (device.registration_status === 'active' &&
        !device.requires_full_resync &&
        device.dataset_epoch === currentDatasetEpoch &&
        compareInstants(now, device.registration_expires_at) < 0);
}
function payloadIdentity(payload) {
    switch (payload.entity_type) {
        case 'shift_aggregate':
            return {
                entity_id: payload.value.shift.shift_id,
                revision: payload.value.aggregate_revision,
                tombstone: payload.value.shift.metadata.tombstone,
            };
        case 'vehicle':
            return metadataIdentity(payload.value.vehicle_id, payload.value.metadata);
        case 'route_variant':
            return metadataIdentity(payload.value.route_variant_id, payload.value.metadata);
        case 'route_template':
            return metadataIdentity(payload.value.route_template_id, payload.value.metadata);
        case 'user_defined_location':
            return metadataIdentity(payload.value.location_id, payload.value.metadata);
        case 'location_merge_decision':
            return metadataIdentity(payload.value.decision_id, payload.value.metadata);
        case 'user_preferences':
            return metadataIdentity(payload.value.user_preferences_id, payload.value.metadata);
    }
}
function metadataIdentity(entityId, metadata) {
    return { entity_id: entityId, revision: metadata.revision, tombstone: metadata.tombstone };
}
//# sourceMappingURL=sync.js.map